import React, {useCallback, useState} from "react";
import {defineMessages, FormattedMessage, useIntl} from "react-intl";
import {FormControl, FormGroup, FormLabel, Row, Col} from "react-bootstrap";
import {CustomerTierInfo, MoneyAsObject} from "../../shared/api/loyaltyApi";
import {FormDefaultContainer} from "../styles";
import {getCurrencyCodeSymbol, getProtoTimestampAsDateString} from "../helper";
import {InfoModal, InfoModalContentProps} from "./InfoModal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";

interface LoyaltyCurrentTierInformationProps {
  data: CustomerTierInfo
}

const messages = defineMessages({
  currentTierLevel: {
    id: "loyaltyCustomerHistorical.currentTierLevel",
    defaultMessage: "Current level",
  },
  levelExpirationDate: {
    id: "loyaltyCustomerHistorical.levelExpirationDate",
    defaultMessage: "Level expiration date",
  },
  currentSpentAmountSum: {
    id: "loyaltyCustomerHistorical.currentSpentAmountSum",
    defaultMessage: "Currently validated amount",
  },
  currentTransactionAmountNumber: {
    id: "loyaltyCustomerHistorical.currentTransactionAmountSum",
    defaultMessage: "Currently validated bookings",
  },
  notExpiresAt: {
    id: "loyaltyCustomerHistorical.notExpiresAt",
    defaultMessage: "No expiration date",
  },
});

function LoyaltyCurrentTierInformation(props: LoyaltyCurrentTierInformationProps): JSX.Element {
  const intl = useIntl();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<InfoModalContentProps>({});

  const getCurrentSpentAmountSum = useCallback((spentAmount?: MoneyAsObject) => {
    if (!spentAmount) {
      return "0 €";
    }
    const currencyCodeSymbol = getCurrencyCodeSymbol(spentAmount.currencyCode);
    return `${currencyCodeSymbol} ${spentAmount.units}`;
  }, []);

  const getLevelExpirationDate = useCallback((timestamp?: any) => {
    return getProtoTimestampAsDateString(timestamp) || intl.formatMessage(messages.notExpiresAt);
  }, [intl]);

  const handleOnClickSpentAmountInfo = useCallback(() => {
    setModalContent({
      spentAmount: {
        pendingAmount: props.data.info?.spentAmount?.pending?.units,
        validatedAmount: props.data.info?.spentAmount?.validated?.units,
        currencyCode: getCurrencyCodeSymbol(props.data.info?.spentAmount?.total?.currencyCode)
      }
    });
    setModalOpen(true);
  }, [props.data.info]);

  const handleOnClickBookingsNumberInfo = useCallback(() => {
    setModalContent({
      transactionsNumber: {
        pendingBookings: props.data.info?.transactionsNumber?.pending,
        validatedBookings: props.data.info?.transactionsNumber?.validated,
      }
    });
    setModalOpen(true);
  }, [props.data.info]);

  return (
    <FormDefaultContainer>
      <FormGroup as={Row} className="mb-3">
        <FormLabel column sm={6}><FormattedMessage {...messages.currentTierLevel} /></FormLabel>
        <Col sm={6}>
          <FormControl disabled value={props.data.tierTicker} />
        </Col>
      </FormGroup>
      <FormGroup as={Row} className="mb-3">
        <FormLabel column sm={6}><FormattedMessage {...messages.levelExpirationDate} /></FormLabel>
        <Col sm={6}>
          <FormControl disabled value={getLevelExpirationDate(props.data.expiresAt)} />
        </Col>
      </FormGroup>
      <FormGroup as={Row} className="mb-3">
        <FormLabel column sm={6}>
          <FormattedMessage {...messages.currentSpentAmountSum} />
          <FontAwesomeIcon
            icon={faInfoCircle}
            color="#B3B3B3"
            className="btn text-primary"
            onClick={handleOnClickSpentAmountInfo}
          />
        </FormLabel>
        <Col sm={6}>
          <FormControl disabled value={getCurrentSpentAmountSum(props.data.info?.spentAmount?.validated)} />
        </Col>
      </FormGroup>
      <FormGroup as={Row} className="mb-0">
        <FormLabel column sm={6}>
          <FormattedMessage {...messages.currentTransactionAmountNumber} />
          <FontAwesomeIcon
            icon={faInfoCircle}
            color="#B3B3B3"
            className="btn text-primary"
            onClick={handleOnClickBookingsNumberInfo}
          />
        </FormLabel>
        <Col sm={6}>
          <FormControl disabled value={props.data.info?.transactionsNumber?.validated} />
        </Col>
      </FormGroup>
      <InfoModal
        isOpen={modalOpen}
        handleOnHideModal={() => setModalOpen(false)}
        content={modalContent}
      />
    </FormDefaultContainer>
  )
}

export default LoyaltyCurrentTierInformation;